import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import "@/index.css";
import "@/i18n";
import App from "@/App";

// CRITICAL — global axios default. Every call from this app to the backend
// must carry the `session_token` httpOnly cookie. Setting this here once
// eliminates the entire class of "I forgot withCredentials on this one call"
// bugs that were causing random 401s and the kick-outs Owen + users reported
// (Feb 24, 2026). 122 of 288 axios calls in the codebase were not passing
// withCredentials individually — this default rescues all of them.
axios.defaults.withCredentials = true;

// Jun 2026 — Cloudflare/k8s ingress fronting *.preview.emergentagent.com
// rewrites `Access-Control-Allow-Origin` to `*`, which browsers reject when
// combined with `Access-Control-Allow-Credentials: true` (manifested as
// "Failed to fetch" on journal PDF downloads). Mitigation: also attach the
// session token as a Bearer header on every request when we have one in
// localStorage. The backend `get_current_user` already supports both the
// httpOnly cookie AND the `Authorization: Bearer …` header, so this is
// purely additive and survives the CORS override.
axios.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('colosseum_session_token');
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch { /* localStorage may be disabled */ }
  return config;
});

// One-shot backfill — for users whose session was issued BEFORE the
// localStorage rollout. Tries to mirror the httpOnly cookie token into
// localStorage so subsequent download fetches (which can't rely on the
// cookie due to the ingress CORS override) can authenticate via Bearer.
// Silently 401s for logged-out visitors — harmless.
(() => {
  try {
    if (localStorage.getItem('colosseum_session_token')) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/session-token`;
    axios.get(url, { withCredentials: true })
      .then((r) => {
        if (r?.data?.session_token) {
          try { localStorage.setItem('colosseum_session_token', r.data.session_token); } catch { /* ignore */ }
        }
      })
      .catch(() => { /* not logged in or no cookie session — ignore */ });
  } catch { /* ignore */ }
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
